import * as React from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { commonStyleSheet } from 'library/common-styles';
import { StylesProps, ThemeType } from 'theme/jss-types';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';
import { NAVBAR_HEIGHT } from 'library/globals';
import { getAppointmentSlots } from 'network/appointments.api';
import { ReduxStore } from 'reducers/redux.types';
import { Master } from 'types/master-data-types';

const styles = (theme: ThemeType) => ({
    ...commonStyleSheet(theme),
    main: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: '5px 6px 6px 3px',
        height: '100%',
        backgroundColor: 'transparent',
    },
});

interface AppointmentsProps extends StylesProps<ReturnType<typeof styles>> {
    master: Master;
}

interface AppointmentSlot {
    id: string;
    slNo: number;
    day: string;
    timeSlot: string;
    address: string;
    taskType?: string;
    tags?: string[] | null;
    serviceTime?: string | number;
}

// Read-only appointment-slots list (HLD ISS-287978 §4.4) — the mutation-free sibling of
// schedule-pickup.tsx. Slot management stays on Excel/ops-dashboard/API per PRD §2.1.
const Appointments = (props: AppointmentsProps) => {
    const { classes, master } = props;
    const { t } = useTranslation();
    const [slots, setSlots] = React.useState<AppointmentSlot[]>([]);
    const [tableLoading, setTableLoading] = React.useState(false);

    const bannerHeight = (master?.config?.customer_portal_config?.password_policy_banner_expiry_date
        && new Date(master?.config?.customer_portal_config?.password_policy_banner_expiry_date) <= new Date()) ? 0 : 50;

    const fetchAppointmentSlots = async () => {
        setTableLoading(true);
        try {
            const response = await getAppointmentSlots();
            if (response.isSuccess) {
                setSlots(response.data?.rules || []);
            } else {
                message.error(response.errorMessage);
            }
        } catch (error) {
            message.error('Failed to fetch appointment slots');
        } finally {
            setTableLoading(false);
        }
    };

    React.useEffect(() => {
        fetchAppointmentSlots();
    }, []);

    const columns = [
        {
            title: t('day'),
            dataIndex: 'day',
            key: 'day',
            width: 100,
        },
        {
            title: t('time_slot'),
            dataIndex: 'timeSlot',
            key: 'timeSlot',
            width: 140,
        },
        {
            title: t('address'),
            dataIndex: 'address',
            key: 'address',
        },
        {
            title: t('tags'),
            dataIndex: 'tags',
            key: 'tags',
            width: 120,
            render: (tags: string[] | null) => (tags?.length ? tags.join(', ') : '-'),
        },
        {
            title: t('direction'),
            dataIndex: 'taskType',
            key: 'taskType',
            width: 110,
            render: (taskType: string) => (taskType === 'delivery' ? t('delivery') : t('pickup')),
        },
        {
            title: t('service_time'),
            dataIndex: 'serviceTime',
            key: 'serviceTime',
            width: 120,
            render: (serviceTime: string | number) => (serviceTime ?? '-'),
        },
    ];

    return (
        <div className={classes.main}>
            <FixedHeaderTable
                bordered={false}
                pagination={false}
                loading={tableLoading}
                dataSource={slots}
                columns={columns}
                rowKey="id"
                scroll={{ y: 400 }}
                style={{
                    height: `calc(
                        (((100vh - ${NAVBAR_HEIGHT}px) - 55px) - ${bannerHeight}px) )`,
                    background: '#ffffff',
                }}
            />
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    return {
        master,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(Appointments);
